import React, { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  Box,
  Button,
  Card,
  CardContent,
  MenuItem,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import {
  getKeepUntilDays,
  updateKeepUntilDays,
} from '@/features/notification-logs/services/notificationLogService';

const i18nBase = 'resources.notification-logs';

const DAY_OPTIONS = Array.from({ length: 11 }, (_, i) => i);

export const KeepUntilBar: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [selected, setSelected] = useState<number>(0);
  const [current, setCurrent] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);

  const loadCurrent = React.useCallback(() => {
    getKeepUntilDays()
      .then(setCurrent)
      .catch(() => setCurrent(0));
  }, []);

  const didLoadRef = React.useRef(false);
  useEffect(() => {
    if (didLoadRef.current) return;
    didLoadRef.current = true;
    loadCurrent();
  }, [loadCurrent]);

  const handleUpdate = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await updateKeepUntilDays(selected);
      setCurrent(selected);
      notify(`${i18nBase}.notifications.updated`, { type: 'info' });
    } catch (err) {
      notify(
        extractErrorMessage(
          err,
          `${i18nBase}.notifications.updateFailed`,
          translate
        ),
        { type: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card sx={{ border: 1, borderColor: 'divider' }}>
      <CardContent
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'flex-start',
          rowGap: 1,
          columnGap: 2,
          py: 1,
          '&:last-child': { pb: 1 },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {translate(`${i18nBase}.keepUntil.label`)}
        </Typography>
        <MuiTextField
          select
          size="small"
          value={selected}
          onChange={(e) => setSelected(Number(e.target.value))}
          sx={{ minWidth: 80 }}
        >
          {DAY_OPTIONS.map((d) => (
            <MenuItem key={d} value={d}>
              {d}
            </MenuItem>
          ))}
        </MuiTextField>
        <Button
          variant="contained"
          size="small"
          onClick={handleUpdate}
          disabled={submitting}
        >
          {translate(`${i18nBase}.keepUntil.update`)}
        </Button>
        <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          {translate(`${i18nBase}.keepUntil.current`, { count: current })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default KeepUntilBar;
